/**
 * API Utility with automatic token handling and 401 interceptor
 */

import { ApiError } from '../types/common';
import i18n from '../i18n';

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || '/api';

interface RequestOptions extends RequestInit {
  requiresAuth?: boolean;
}

function buildHeaders(source?: HeadersInit): Headers {
  const headers = new Headers();

  if (!source) {
    return headers;
  }

  if (source instanceof Headers) {
    source.forEach((value, key) => {
      if (value !== undefined && value !== null && value !== 'undefined') {
        headers.set(key, value);
      }
    });
    return headers;
  }

  if (Array.isArray(source)) {
    source.forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== 'undefined') {
        headers.set(key, value);
      }
    });
    return headers;
  }

  Object.entries(source).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== 'undefined') {
      headers.set(key, String(value));
    }
  });

  return headers;
}

/**
 * Get current dashboard language for API requests
 */
function getCurrentLanguage(): string {
  return i18n.language || 'en';
}

/**
 * Custom fetch wrapper with authentication and error handling
 */
export async function apiRequest<T = any>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { requiresAuth = true, ...fetchOptions } = options;
  const { headers: customHeaders, ...restOptions } = fetchOptions;

  // Get token from localStorage
  const token = localStorage.getItem('token');

  const isFormData = restOptions.body instanceof FormData;
  const headers = buildHeaders(customHeaders);

  if (!isFormData && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  // Add Authorization header if token exists and auth is required
  if (requiresAuth && token) {
    headers.set('Authorization', `Token ${token}`);
  }

  // Add Accept-Language header for multilingual support
  // Backend uses this to return translated data based on dashboard language
  if (!headers.has('Accept-Language')) {
    headers.set('Accept-Language', getCurrentLanguage());
  }

  const url = `${API_BASE_URL}${endpoint}`;

  // 🔍 API Debugging: Log request details
  const appSection = endpoint.split('/').filter(Boolean)[0] || 'unknown';
  console.group(`🌐 API Request: ${restOptions.method || 'GET'}`);
  console.log('📍 URL:', url);
  console.log('📦 App Section:', appSection);
  console.log('🔑 Endpoint:', endpoint);
  console.log('🔐 Authenticated:', requiresAuth && !!token);
  if (restOptions.body && !isFormData) {
    try {
      console.log('📤 Request Body:', JSON.parse(restOptions.body as string));
    } catch {
      console.log('📤 Request Body:', restOptions.body);
    }
  } else if (isFormData) {
    console.log('📤 Request Body: [FormData]');
  }
  console.groupEnd();

  try {
    const response = await fetch(url, {
      ...restOptions,
      headers,
    });

    // Handle 401 Unauthorized - token expired or invalid
    if (response.status === 401) {
      const isOnLoginPage = window.location.pathname === '/login';

      if (!isOnLoginPage) {
        console.warn('🔒 Authentication failed - redirecting to login');

        // Prevent double logout using sessionStorage flag
        const isAlreadyLoggingOut = sessionStorage.getItem('logging_out');

        if (!isAlreadyLoggingOut) {
          sessionStorage.setItem('logging_out', 'true');

          // Clear user data
          localStorage.removeItem('token');
          localStorage.removeItem('user');

          // Small delay to prevent race conditions
          setTimeout(() => {
            sessionStorage.removeItem('logging_out');
            window.location.href = '/login';
          }, 100);
        }
      }

      // Always throw ApiError (not plain Error) so the catch block
      // does not misidentify it as a network error
      throw new ApiError('Authentication required', 401, { isAuthError: true });
    }

    // Handle other HTTP errors
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        error: `HTTP ${response.status}: ${response.statusText}`,
      }));
      
      // 🔍 API Debugging: Log error response
      console.group(`❌ API Error Response: ${response.status}`);
      console.log('📍 URL:', url);
      console.log('📦 App Section:', appSection);
      console.log('💥 Error Data:', errorData);
      console.groupEnd();
      
      throw new ApiError(
        errorData.error || errorData.message || 'Request failed',
        response.status,
        errorData
      );
    }

    const contentType = response.headers.get('Content-Type');
    const text = await response.text();

    console.log('🔍 Response text length:', text?.length || 0);
    console.log('🔍 Response text preview:', text?.substring(0, 200));

    // Detect HTML response (backend not reachable or proxy misconfigured)
    const trimmed = text?.trimStart() ?? '';
    if (
      trimmed.startsWith('<!doctype') ||
      trimmed.startsWith('<!DOCTYPE') ||
      trimmed.startsWith('<html')
    ) {
      console.error('🚨 Backend returned HTML instead of JSON — proxy or backend may be down', { url });
      throw new ApiError(
        'سرور در دسترس نیست. لطفاً بعداً دوباره امتحان کنید.',
        503,
        { hint: 'HTML response received instead of JSON', url }
      );
    }

    let parsedData: T;
    if (!text) {
      parsedData = undefined as T;
    } else if (contentType && contentType.includes('application/json')) {
      parsedData = JSON.parse(text) as T;
    } else {
      try {
        parsedData = JSON.parse(text) as T;
      } catch {
        parsedData = text as unknown as T;
      }
    }

    // 🔍 API Debugging: Log success response
    console.group(`✅ API Success Response: ${response.status}`);
    console.log('📍 URL:', url);
    console.log('📦 App Section:', appSection);
    console.log('📥 Response Data:', parsedData);
    console.groupEnd();

    return parsedData;
  } catch (error) {
    // 🔍 API Debugging: Log network error
    if (!(error instanceof ApiError)) {
      console.group('🚨 API Network Error');
      console.log('📍 URL:', url);
      console.log('📦 App Section:', appSection);
      console.error('💥 Error:', error);
      console.groupEnd();
      
      // Create user-friendly error message for network errors
      const networkError = new ApiError(
        i18n.t('toastMessages.networkErrorDetail'),
        0,
        { originalError: error, endpoint, appSection }
      );
      throw networkError;
    }
    throw error;
  }
}

/**
 * Helper methods for common HTTP verbs
 */
export const api = {
  get: <T = any>(endpoint: string, options?: RequestOptions) =>
    apiRequest<T>(endpoint, { ...options, method: 'GET' }),

  post: <T = any>(endpoint: string, data?: any, options?: RequestOptions) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data instanceof FormData ? data : (data ? JSON.stringify(data) : undefined),
    }),

  put: <T = any>(endpoint: string, data?: any, options?: RequestOptions) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data instanceof FormData ? data : (data ? JSON.stringify(data) : undefined),
    }),

  delete: <T = any>(endpoint: string, options?: RequestOptions) =>
    apiRequest<T>(endpoint, { ...options, method: 'DELETE' }),

  patch: <T = any>(endpoint: string, data?: any, options?: RequestOptions) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data instanceof FormData ? data : (data ? JSON.stringify(data) : undefined),
    }),
};

/**
 * Check if token is expired (for JWT tokens)
 * Returns true if token is expired or invalid
 */
export function isTokenExpired(token: string): boolean {
  try {
    // If using JWT, decode and check expiration
    // Django Token Authentication doesn't have built-in expiration
    // You might need to implement this based on your backend setup
    
    // For now, we rely on backend 401 response
    return false;
  } catch {
    return true;
  }
}

/**
 * Get stored token
 */
export function getToken(): string | null {
  return localStorage.getItem('token');
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  const token = getToken();
  return !!token && !isTokenExpired(token);
}
